import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ButtonGroup } from '@/components/ui/button-group';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import { getUserFriendlyErrorMessage, isChunkLoadError, isNetworkError, logError } from '@/utils/error-logging';
import { AlertTriangle, Bug, Home, RefreshCw, WifiOff } from 'lucide-react';
import { Trans, useLingui } from '@lingui/react/macro';

interface RouterErrorBoundaryProps {
	error: unknown;
	reset?: () => void;
	info?: { componentStack?: string };
}

function toError(value: unknown): Error {
	if (value instanceof Error) return value;
	return new Error(typeof value === 'string' ? value : 'An unknown error occurred');
}

/**
 * Primary error boundary component used for route-level and Sentry error boundaries.
 * Shows a dev-friendly view in development and a user-friendly view in production.
 * Classifies errors into network, chunk load, and generic categories.
 */
export function RouterErrorBoundary({ error: rawError, reset, info }: RouterErrorBoundaryProps) {
	const error = toError(rawError);
	const handleReload = () => {
		window.location.reload();
	};

	const handleGoHome = () => {
		window.location.href = '/';
	};

	const handleReset = () => {
		try {
			reset?.();
		} catch {
			handleReload();
		}
	};

	const isDevelopment =
		import.meta.env.DEV || import.meta.env.VITE_APP_ENV === 'e2e' || import.meta.env.VITE_APP_ENV === 'staging';

	logError(error, info);

	return isDevelopment ? (
		<DevelopmentError error={error} info={info} onReset={handleReset} onReload={handleReload} onGoHome={handleGoHome} />
	) : (
		<ProductionError error={error} onReset={handleReset} onGoHome={handleGoHome} />
	);
}

// --- Development Error View ---

function DevelopmentError({
	error,
	info,
	onReset,
	onReload,
	onGoHome,
}: {
	error: Error;
	info?: { componentStack?: string };
	onReset: () => void;
	onReload: () => void;
	onGoHome: () => void;
}) {
	return (
		<div className="min-h-screen bg-red-50 dark:bg-red-950/20 p-4 flex items-center justify-center">
			<Card className="w-full max-w-4xl border-red-200 dark:border-red-800 pt-0 gap-0">
				<CardHeader className="border-b border-red-200 dark:border-red-800 bg-red-100/50 dark:bg-red-900/20 py-3 sm:py-4">
					<div className="flex items-center gap-2">
						<Bug className="h-5 w-5 sm:h-6 sm:w-6 text-red-600 shrink-0" />
						<div className="min-w-0">
							<CardTitle className="text-red-900 dark:text-red-300 text-sm sm:text-base">Development Error</CardTitle>
							<CardDescription className="text-red-700 dark:text-red-400 text-xs sm:text-sm truncate">
								<Trans>An error occurred while rendering this component</Trans>
							</CardDescription>
						</div>
					</div>
				</CardHeader>
				<CardContent className="space-y-3 sm:space-y-4 p-3 sm:p-6">
					<Alert variant="destructive">
						<AlertTriangle className="h-4 w-4" />
						<AlertDescription className="font-mono text-xs sm:text-sm break-all">
							{error.name}: {error.message}
						</AlertDescription>
					</Alert>

					{info?.componentStack && (
						<Collapsible>
							<CollapsibleTrigger
								render={<Button variant="outline" className="w-full justify-between text-xs sm:text-sm" />}
							>
								<span>Component Stack</span>
								<span className="text-xs">Click to expand</span>
							</CollapsibleTrigger>
							<CollapsibleContent>
								<div className="mt-2 p-2 sm:p-4 bg-gray-100 dark:bg-gray-800 rounded-lg overflow-auto max-h-48 sm:max-h-none">
									<pre className="text-xs whitespace-pre-wrap break-all">{info.componentStack}</pre>
								</div>
							</CollapsibleContent>
						</Collapsible>
					)}

					{error.stack && (
						<Collapsible>
							<CollapsibleTrigger
								render={<Button variant="outline" className="w-full justify-between text-xs sm:text-sm" />}
							>
								<span>Stack Trace</span>
								<span className="text-xs">Click to expand</span>
							</CollapsibleTrigger>
							<CollapsibleContent>
								<div className="mt-2 p-2 sm:p-4 bg-gray-900 text-green-400 rounded-lg overflow-auto max-h-48 sm:max-h-none">
									<pre className="text-xs whitespace-pre-wrap break-all">{error.stack}</pre>
								</div>
							</CollapsibleContent>
						</Collapsible>
					)}

					<ButtonGroup orientation="horizontal" className="w-full pt-2 sm:pt-4">
						<Button onClick={onReset} size="sm" className="flex-1">
							<RefreshCw className="h-4 w-4 mr-2" />
							<span className="sm:hidden">Reset</span>
							<span className="hidden sm:inline">Reset Component</span>
						</Button>
						<Button onClick={onReload} variant="outline" size="sm" className="flex-1">
							<RefreshCw className="h-4 w-4 mr-2" />
							<span className="sm:hidden">Reload</span>
							<span className="hidden sm:inline">Reload Page</span>
						</Button>
						<Button onClick={onGoHome} variant="outline" size="sm" className="flex-1">
							<Home className="h-4 w-4 mr-2" />
							<span className="sm:hidden">Home</span>
							<span className="hidden sm:inline">Go Home</span>
						</Button>
					</ButtonGroup>
				</CardContent>
			</Card>
		</div>
	);
}

// --- Production Error View ---

function ProductionError({ error, onReset, onGoHome }: { error: Error; onReset: () => void; onGoHome: () => void }) {
	const { t } = useLingui();
	const isNetwork = isNetworkError(error);
	const isChunkLoad = isChunkLoadError(error);

	const icon = isNetwork ? (
		<WifiOff className="h-8 w-8 text-red-600" />
	) : isChunkLoad ? (
		<RefreshCw className="h-8 w-8 text-blue-600" />
	) : (
		<AlertTriangle className="h-8 w-8 text-red-600" />
	);

	const title = isNetwork ? t`Connection Problem` : isChunkLoad ? t`Update Available` : t`Something went wrong`;

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 p-4 flex items-center justify-center">
			<Card className="w-full max-w-sm sm:max-w-md text-center">
				<CardHeader className="space-y-3 sm:space-y-4 px-4 sm:px-6">
					<div
						className={cn(
							'mx-auto w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center',
							isChunkLoad ? 'bg-blue-100 dark:bg-blue-900/30' : 'bg-red-100 dark:bg-red-900/30',
						)}
					>
						{icon}
					</div>
					<div>
						<CardTitle className="text-xl sm:text-2xl font-bold">{title}</CardTitle>
						<CardDescription className="mt-2 text-sm">{getUserFriendlyErrorMessage(error)}</CardDescription>
					</div>
				</CardHeader>
				<CardContent className="space-y-3 sm:space-y-4 px-4 sm:px-6">
					<ButtonGroup orientation="horizontal" className="w-full">
						<Button
							onClick={onReset}
							size="sm"
							className={cn('flex-1', isChunkLoad && 'bg-blue-600 hover:bg-blue-700')}
						>
							<RefreshCw className="h-4 w-4 mr-2" />
							{isChunkLoad ? t`Refresh to Update` : t`Try Again`}
						</Button>
						{!isNetwork && (
							<Button onClick={onGoHome} variant="outline" size="sm" className="flex-1">
								<Home className="h-4 w-4 mr-2" />
								<Trans>Go to Homepage</Trans>
							</Button>
						)}
						{isNetwork && (
							<Button variant="outline" size="sm" className="flex-1" disabled>
								<WifiOff className="h-4 w-4 mr-2" />
								<Trans>Check Connection</Trans>
							</Button>
						)}
					</ButtonGroup>
					<p className="text-xs text-muted-foreground mt-4">Error ID: {Date.now().toString(36).toUpperCase()}</p>
				</CardContent>
			</Card>
		</div>
	);
}

// Legacy default export for backwards compatibility with existing Sentry fallback usage
export default function ErrorBoundaryPage({
	error,
	resetErrorBoundary,
}: {
	error: unknown;
	resetErrorBoundary: () => void;
}) {
	return <RouterErrorBoundary error={error} reset={resetErrorBoundary} />;
}
